import { useAuth } from "../../../../hooks/AuthenticateContext";
import { ValueFormatterParams } from "ag-grid-community";
import React, { useCallback, useRef, useState } from "react";
import Grid from "../../../../components/Grid";
import { ColumnDef } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import {
  formatDateTimeBR,
  getCargoTypes,
  getOperationTypes,
  maskCnpj,
  renderCargoTypes,
  renderOperationTypes,
} from "../../../../helpers/format";
import { STATUS_OPERACOES_PORTO_AGENDADA } from "../../../../helpers/status";
import { useModal } from "../../../../hooks/ModalContext";
import api from "../../../../services/api";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import Create from "./Create";
import Info from "./Info";
import { IOperacoesPortoAgendada } from "./types/types";
import Can from "../../../../components/Can";

// import { Container } from './styles';

const OperacoesPortoAgendada: React.FC = () => {
  const { user } = useAuth();
  const [columns] = useState<ColumnDef[]>([
    {
      field: "data_agendamento_terminal",
      headerName: "Data/Hora",
      filter: true,
      type: "dateColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if(params.value) {
          return formatDateTimeBR(params.value)
        }
        return '---';
      }
    },
    {
      field: "placa_dianteira_veiculo",
      fieldName: "placa",
      filter: true,
      headerName: "Placa Dianteira",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "placa_traseira_veiculo",
      fieldName: "placa",
      filter: true,
      headerName: "Placa Traseira",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "cpf_motorista",
      filter: true,
      headerName: "CPF Motorista",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "cnpj_transportadora",
      headerName: "CNPJ Transportadora",
      valueFormatter: (params: ValueFormatterParams) => {
        if(params.value) {
          return maskCnpj(params.value);
        }
        return '---';
      }
    },
    {
      field: "terminal.razao_social",
      fieldName: "terminal",
      headerName: "Terminal",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if(params.value) {
          return params.value;
        }
        return '---';
      }
    },
    {
      field: "tipo_carga",
      fieldName: "tipo_carga",
      headerName: "Tipo de Carga",
      filter: true,
      filterParams: {
        selected: {
          isMultiple: false,
          data: getCargoTypes()
        }
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return String(renderCargoTypes(params.value).replace(",", " "));
        }
        return "---";
      },
    },
    {
      field: "tipo_operacao",
      headerName: "Tipo da Operação",
      filter: true,
      filterParams: {
        selected: {
          isMultiple: false,
          data: getOperationTypes()
        }
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return renderOperationTypes(params.value);
        }
        return "---";
      },
    },
    {
      field: "identificadores_conteineres",
      filter: true,
      headerName: "Identificadores Conteineres",
    },
    {
      field: "tolerancia_inicio_agendamento",
      headerName: "Tolerância Inicial",
    },
    {
      field: "tolerancia_fim_agendamento",
      headerName: "Tolerância Final",
    },
    {
      field: "usuario",
      headerName: "Usuário",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
  ]);

  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<IOperacoesPortoAgendada>();
  const [isView, setIsView] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { isModalOpen, openModal, closeModal } = useModal();

  const gridRef = useRef<any>();

  const defaultFilters = {
    "id_transportadora": user?.id_transportadora
  };

  const onDelete = useCallback(async (rowId?: number) => {
    try {
      setLoading(true);
      const body = {
        id_operacao_porto_agendada: rowId,
      };

      await api.post("/deletar/operacaoPortoAgendada", body);

      setLoading(false);

      setIsRemove(false);

      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, []);

  return (
    <div className="flex flex-col w-full h-screen bg-[#F5F5F5] p-5">
      <Loading loading={loading} />
      {isModalOpen && (
        <Create
          isEdit={isEdit}
          isView={isView}
          selectedRow={selectedRow}
          onClear={() => closeModal()}
          onConfirm={() => {
            closeModal();
            gridRef.current?.refresh();
          }}
        />
      )}

      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_operacao_porto_agendada)}
          row={selectedRow?.placa_dianteira_veiculo}
        />
      )}

      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Agendamento"
          onClose={() => setIsView(!isView)}
        />
      )}

      <div className="flex items-center justify-between w-full mb-3">
        <div>
          <h1 className="text-2xl text-[#000000] font-bold">Operações Porto Agendada</h1>
        </div>
        <div className="flex items-center gap-3">
          <Can acao="SALVAR">
          <button
            className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full gap-2 hover:bg-[#0a3d6a] transition-colors"
            onClick={() => {
              setIsEdit(false);
              setIsView(false);
              setSelectedRow(undefined);
              openModal();
            }}
            id="btnCreate"
          >
            Adicionar <img src={PlusButtonIcon} alt="" className="ml-1" />
          </button>
          </Can>
        </div>
      </div>
      <div className="flex w-[calc(100vh - 90px)]">
        <Grid
          ref={gridRef}
          columns={columns}
          filters={[]}
          customFilters={defaultFilters}
          pagination
          path="/listar/operacaoPortoAgendada"
          onDelete={(data: any) => {
            setIsRemove(!isRemove);
            setSelectedRow(data);
          }}
          isShowStatus
          status={STATUS_OPERACOES_PORTO_AGENDADA}
          onView={(data: any) => {
            setSelectedRow(data);
            setIsView(!isView);
          }}
          onUpdate={(data: any) => {
            setSelectedRow(data);
            setIsEdit(!isEdit);
            openModal();
          }}
        />
      </div>
    </div>
  );
};

export default OperacoesPortoAgendada;
